/**
 * ADOBE CONFIDENTIAL
 * __________________
 * Copyright 2023 Adobe
 * All Rights Reserved.
 * __________________
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 */

import { Initializer } from '@adobe-commerce/elsie/lib';
import { Lang } from '@adobe-commerce/elsie/i18n';
import { ProductModel, Option } from '@/pdp/data/models';
import {
  fetchProductData,
  setProductConfigurationValid,
  setProductConfigurationValues,
} from '@/pdp/api';
import { events } from '@adobe-commerce/event-bus';
import {
  getUrlParams,
  getValidatedQuantityFromUrl,
} from '@/pdp/lib/url-params';
import { publishProductPageViewEvent } from '@/pdp/lib/acdl';
import { transformProductData } from '@/pdp/data/transforms/product-transform';

export type OptionsTransformer = (options: Option[]) => Option[];

type ModelConfig = {
  initialData?: any;
  /** @deprecated Use "transformer" instead */
  transform?: (data?: ProductModel) => ProductModel;
  transformer?: (data?: ProductModel) => ProductModel;
  fallbackData?: (
    parentProduct: any,
    simpleProduct: ProductModel
  ) => ProductModel;
};

type ProductOptionsConfig = {
  optionsTransformer?: OptionsTransformer;
};

type ConfigProps = {
  scope?: string;
  langDefinitions?: Lang;
  defaultLocale?: string;
  globalLocale?: string;
  sku?: string;
  acdl?: boolean;
  anchors?: string[];
  persistURLParams?: boolean;
  preselectFirstOption?: boolean;
  optionsUIDs?: string[];
  models?: {
    ProductDetails?: ModelConfig;
    ProductOptions?: ProductOptionsConfig;
    [name: string]: ModelConfig | ProductOptionsConfig | undefined;
  };
};

export const initialize = new Initializer<ConfigProps>({
  init: async (_config) => {
    const initialData = _config?.models?.ProductDetails?.initialData;

    // Get URL parameters safely
    const urlParams = getUrlParams() || {};

    // Get optionsUIDs from URL params or config
    const optionsUIDs =
      urlParams.optionsUIDs?.split(',') || initialData?.optionsUIDs;

    // Get and validate quantity from URL params
    const initialQuantity = getValidatedQuantityFromUrl(1); // Default quantity to 1 - Ensures backward compatibility

    // Set default config
    const defaultConfig: ConfigProps = {
      defaultLocale: 'en-US',
      globalLocale: 'en-US',
      persistURLParams: false,
      acdl: false,
      optionsUIDs,
    };

    const config = { ...defaultConfig, ..._config };

    initialize.config.setConfig({ ...config });

    if (config?.sku) {
      // Get PDP data
      const data = initialData
        ? transformProductData(initialData)
        : await fetchProductData(config.sku, {
            preselectFirstOption: config.preselectFirstOption,
          });

      // Emit PDP initial data
      events.emit('pdp/data', data, { scope: config.scope });

      // Set default values for product configuration
      const defaultValues = {
        sku: config.sku,
        quantity: initialQuantity, // Use the determined quantity from URL params or default to 1
        optionsUIDs: data?.optionUIDs,
      };

      setProductConfigurationValues(() => ({ ...defaultValues }), {
        scope: config.scope,
      });

      setProductConfigurationValid(
        () => {
          // configurable product, check if all options are selected
          if (data?.options) {
            // all selections should be made
            return data.options.length === data?.optionUIDs?.length;
          }

          // simple product
          return true;
        },
        { scope: config.scope }
      );

      // Publish ACDL event
      if (config.acdl && data) {
        publishProductPageViewEvent(data);
      }
    }
  },

  listeners: () => [
    events.on('locale', async () => {
      const { sku, scope } = initialize.config.getConfig();

      if (sku) {
        const next = await fetchProductData(sku);
        events.emit('pdp/data', next, { scope });
      }
    }),
  ],
});

export const config = initialize.config;
